import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebase-config';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';

export default function AuthForm({
    modeOverride,
    showModeToggle = true,
    showResetPassword = true,
    showGoogle = true,
    showDebugActions = true,
    showProfileLink = true,
    layout = "inline",
    title,
}) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [practiceName, setPracticeName] = useState('');
    const [mode, setMode] = useState(modeOverride || 'login');
    const [user, setUser] = useState(null);
    const [busy, setBusy] = useState(false);
    const [practiceNameDisplay, setPracticeNameDisplay] = useState("");

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => setUser(u));
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!user) {
            setPracticeNameDisplay("");
            return undefined;
        }

        const ref = doc(db, "user_profiles", user.uid);
        const unsub = onSnapshot(ref, (snap) => {
            const data = snap.data() || {};
            const profile = data.profile || {};
            setPracticeNameDisplay(profile.practiceName || "");
        }, (error) => {
            console.error("Failed to load practice name", error);
            setPracticeNameDisplay("");
        });

        return () => unsub();
    }, [user]);

    useEffect(() => {
        if (modeOverride) setMode(modeOverride);
    }, [modeOverride]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === 'signup') {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const profilePayload = {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    phone: phoneNumber.trim(),
                    practiceName: practiceName.trim(),
                    email: userCredential.user.email,
                };
                // Create Firestore document for the new user
                await setDoc(doc(db, 'users', userCredential.user.uid), {
                    email: userCredential.user.email,
                    createdAt: new Date().toISOString(),
                    uid: userCredential.user.uid
                });
                await setDoc(doc(db, 'user_profiles', userCredential.user.uid), {
                    profile: profilePayload,
                    profileUpdatedAt: serverTimestamp(),
                }, { merge: true });
                console.log('User document created in Firestore!');
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            setEmail('');
            setPassword('');
            setFirstName('');
            setLastName('');
            setPhoneNumber('');
            setPracticeName('');
        } catch (err) {
            console.error('Error:', err);
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    const resetPassword = async () => {
        if (!email) return alert('Enter your email to reset password');
        setBusy(true);
        try {
            await sendPasswordResetEmail(auth, email);
            alert('Password reset email sent');
        } catch (err) {
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    const signInWithGoogle = async () => {
        setBusy(true);
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
        } catch (err) {
            console.error('Google sign-in error:', err);
            // If popups are blocked or cancelled, try redirect fallback
            if (err && (err.code === 'auth/popup-blocked' || err.code === 'auth/cancelled-popup-request' || err.code === 'auth/operation-not-supported-in-this-environment')) {
                try {
                    const provider = new GoogleAuthProvider();
                    await signInWithRedirect(auth, provider);
                    return;
                } catch (rerr) {
                    console.error('Google redirect sign-in also failed:', rerr);
                    alert(`Google sign-in failed: ${rerr?.code || rerr?.message || rerr}`);
                    return;
                }
            }
            // Show a clearer message including the Firebase error code
            alert(`Google sign-in failed: ${err?.code || ''} ${err?.message || ''}`);
        } finally {
            setBusy(false);
        }
    };

    const doSignOut = async () => {
        try {
            await signOut(auth);
        } catch (err) {
            console.error('Sign-out failed', err);
        }
    };

    // Test function to verify Firestore write
    const testFirestoreWrite = async () => {
        if (!user) {
            alert('You must be signed in to test Firestore write');
            return;
        }

        setBusy(true);
        try {
            console.log('Testing Firestore write...');
            console.log('User UID:', user.uid);
            console.log('User email:', user.email);

            await setDoc(doc(db, 'users', user.uid), {
                email: user.email,
                createdAt: new Date().toISOString(),
                uid: user.uid,
                lastUpdated: new Date().toISOString()
            });

            console.log('✅ Firestore write successful!');
            alert('✅ Firestore write successful! Check Firebase Console > Firestore Database for "users" collection');
        } catch (err) {
            console.error('❌ Firestore write error:', err);
            alert(`❌ Firestore write failed: ${err.message}`);
        } finally {
            setBusy(false);
        }
    };

    if (user) {
        return (
            <div className="nav-user">
                <span className="practice-pill">{practiceNameDisplay || "practice_name"}</span>
                {showProfileLink && (
                    <Link className="profile-link profile-button" to="/profile">
                        Profile
                    </Link>
                )}
                <button onClick={doSignOut} className="btn-outline">Sign out</button>
                {showDebugActions && (
                    <>
                        <button
                            onClick={testFirestoreWrite}
                            disabled={busy}
                            style={{ padding: '0.25rem 0.5rem', backgroundColor: '#4CAF50', color: 'white' }}
                        >
                            🧪 Test Firestore Write
                        </button>
                        <button
                            onClick={async () => {
                                try {
                                    const token = await auth.currentUser?.getIdToken();
                                    if (!token) return alert('No ID token available');
                                    await navigator.clipboard.writeText(token);
                                    alert('ID token copied to clipboard (expires in ~1 hour)');
                                } catch (err) {
                                    console.error('Failed to copy ID token', err);
                                    alert('Failed to copy ID token: ' + (err?.message || err));
                                }
                            }}
                            style={{ padding: '0.25rem 0.5rem' }}
                        >
                            Copy ID token
                        </button>
                    </>
                )}
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className={layout === "stacked" ? "auth-form-stacked" : "auth-form-inline"}
        >
            {title && <div style={{ width: '100%', fontWeight: 600 }}>{title}</div>}
            {mode === 'signup' && (
                <>
                    <input
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="auth-input"
                        required
                    />
                    <input
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="auth-input"
                        required
                    />
                    <input
                        type="tel"
                        placeholder="Phone number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="auth-input"
                        required
                    />
                    <input
                        type="text"
                        placeholder="Practice name"
                        value={practiceName}
                        onChange={(e) => setPracticeName(e.target.value)}
                        className="auth-input"
                        required
                    />
                </>
            )}
            <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
            />
            <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                required
            />
            <button type="submit" disabled={busy} className="btn-primary">
                {mode === 'signup' ? 'Sign up' : 'Sign in'}
            </button>
            {showModeToggle && !modeOverride && (
                <button type="button" onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} className="btn-outline">
                    {mode === 'signup' ? 'Use existing account' : 'Create account'}
                </button>
            )}
            {showResetPassword && mode === 'login' && (
                <button type="button" onClick={resetPassword} className="btn-outline">
                    Reset password
                </button>
            )}
            {showGoogle && (
                <button type="button" onClick={signInWithGoogle} className="btn-outline">
                    Sign in with Google
                </button>
            )}
        </form>
    );
}
